import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RadioButtonComponent } from '@libs/ui-lib';
import { TranslateModule } from '@ngx-translate/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TimePattern, TimePatternForm, TimePatternFormGroup } from '@libs/store';
import { AppointmentTimePattern } from '@ffd/api-client';

@Component({
  selector: 'lib-time-pattern-editor',
  templateUrl: './time-pattern-editor.component.html',
  styleUrl: './time-pattern-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RadioButtonComponent, TranslateModule, ReactiveFormsModule],
})
export class TimePatternEditorComponent implements OnInit {
  private _timePatterns: TimePattern[] = [];

  @Input() public set timePatterns(value: TimePattern[]) {
    this._timePatterns = value;
  }
  get timePatterns(): TimePattern[] {
    return this._timePatterns;
  }

  @Output() public timePatternChange: EventEmitter<TimePattern[]> = new EventEmitter<TimePattern[]>();

  constructor(private readonly cdr: ChangeDetectorRef) {}

  private updateTimePatterns(newValue: TimePattern[]): void {
    this.timePatterns = newValue;
    this.cdr.markForCheck();
  }

  private readonly destroyRef = inject(DestroyRef);

  public AppointmentTimePattern = AppointmentTimePattern;
  public timePatternOptions = [AppointmentTimePattern.PROVIDER, AppointmentTimePattern.ASSISTANT, AppointmentTimePattern.CHAIR];

  private readonly emptyFormGroup: TimePatternFormGroup = new FormGroup({
    [AppointmentTimePattern.PROVIDER]: new FormControl(true, { nonNullable: true }),
    [AppointmentTimePattern.ASSISTANT]: new FormControl(false, { nonNullable: true }),
    [AppointmentTimePattern.CHAIR]: new FormControl(false, { nonNullable: true }),
  });

  public timePatternForm: TimePatternForm = new FormGroup({
    bulkSelection: this.emptyFormGroup,
    timePatterns: new FormArray([this.emptyFormGroup]),
  });

  public get timePatternInputs(): FormArray<TimePatternFormGroup> {
    return this.timePatternForm.controls.timePatterns;
  }

  public get bulkSelectionInputs(): TimePatternFormGroup {
    return this.timePatternForm.controls.bulkSelection;
  }

  public ngOnInit(): void {
    this.initForm();
    this.subscribeToBulkSelectionChanges();
    this.subscribeToTimePatternChanges();
  }

  private readonly initForm = () => {
    this.timePatternForm = new FormGroup({
      bulkSelection: new FormGroup({
        [AppointmentTimePattern.PROVIDER]: new FormControl(
          this.timePatterns.every((pattern) => pattern.value === AppointmentTimePattern.PROVIDER),
          { nonNullable: true },
        ),
        [AppointmentTimePattern.ASSISTANT]: new FormControl(
          this.timePatterns.every((pattern) => pattern.value === AppointmentTimePattern.ASSISTANT),
          { nonNullable: true },
        ),
        [AppointmentTimePattern.CHAIR]: new FormControl(
          this.timePatterns.every((pattern) => pattern.value === AppointmentTimePattern.CHAIR),
          { nonNullable: true },
        ),
      }),
      timePatterns: new FormArray(
        this.timePatterns.map(
          (pattern) =>
            new FormGroup({
              [AppointmentTimePattern.PROVIDER]: new FormControl(pattern.value === AppointmentTimePattern.PROVIDER, { nonNullable: true }),
              [AppointmentTimePattern.ASSISTANT]: new FormControl(pattern.value === AppointmentTimePattern.ASSISTANT, {
                nonNullable: true,
              }),
              [AppointmentTimePattern.CHAIR]: new FormControl(pattern.value === AppointmentTimePattern.CHAIR, { nonNullable: true }),
            }),
        ),
      ),
    });
  };

  private readonly subscribeToBulkSelectionChanges = () => {
    for (const selectedColumnControlName in this.bulkSelectionInputs.controls) {
      const selectedColumnControl = this.bulkSelectionInputs.get(selectedColumnControlName);

      selectedColumnControl?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.bulkSelectColumn(selectedColumnControlName);
        this.emitNewTimePatternsAfterBulkSelectionChange(selectedColumnControlName);
      });
    }
  };

  private readonly bulkSelectColumn = (selectedColumnControlName: string) => {
    const emptyValue = {
      [AppointmentTimePattern.PROVIDER]: false,
      [AppointmentTimePattern.ASSISTANT]: false,
      [AppointmentTimePattern.CHAIR]: false,
    };
    const newRowValue = { ...emptyValue, [selectedColumnControlName]: true };

    // set new value in time pattern rows
    this.timePatternInputs.controls.forEach((control) => {
      control.setValue(newRowValue, { emitEvent: false });
    });

    // set new value in bulk selectors
    this.bulkSelectionInputs.setValue(newRowValue, { emitEvent: false });
  };

  private readonly emitNewTimePatternsAfterBulkSelectionChange = (selectedColumnControlName: string) => {
    this.updateTimePatterns(
      this.timePatterns.map((pattern) => ({ ...pattern, value: selectedColumnControlName as AppointmentTimePattern })),
    );
    this.timePatternChange.emit(this.timePatterns);
  };

  private readonly subscribeToTimePatternChanges = () => {
    this.timePatternInputs.controls.forEach((row, index) => {
      for (const selectedColumnControlName in row.controls) {
        row
          .get(selectedColumnControlName)
          ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.changeSelectedColumnBulkSelectionToTrue(selectedColumnControlName);

            for (const columnName in row.controls) {
              if (columnName !== selectedColumnControlName) {
                this.deselectColumn(row, columnName);
              }
            }

            this.emitNewTimePatternsAfterRowChange(index, selectedColumnControlName);
          });
      }
    });
  };

  private readonly deselectColumn = (row: TimePatternFormGroup, columnName: string) => {
    // set other column in bulk selectors
    this.timePatternForm.controls.bulkSelection.get(columnName)?.setValue(false, { emitEvent: false });
    // set other column in time pattern row
    row.get(columnName)?.setValue(false, { emitEvent: false });
  };

  private readonly changeSelectedColumnBulkSelectionToTrue = (selectedColumnControlName: string) => {
    // check selected column in all time pattern rows
    const allRowsAreSelected = this.timePatternInputs
      .getRawValue()
      .every((control) => control[selectedColumnControlName as AppointmentTimePattern]);
    if (allRowsAreSelected) {
      // set selected column to true in bulk selectors
      this.timePatternForm.controls.bulkSelection.get(selectedColumnControlName)?.setValue(true, { emitEvent: false });
    }
  };

  private readonly emitNewTimePatternsAfterRowChange = (index: number, selectedColumnControlName: string) => {
    this.updateTimePatterns(
      this.timePatterns.map((pattern, i) => {
        if (i === index) {
          return { ...pattern, value: selectedColumnControlName as AppointmentTimePattern };
        }
        return pattern;
      }),
    );
    this.timePatternChange.emit(this.timePatterns);
  };
}
